import { useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./AddDomainForm.module.css";
import Button from "../../UI/Button/Button";
import FeedbackModal from "../../UI/FeedbackModal/FeedbackModal";
import { createDomainPlan } from "../../../services/domain-plan-service";

function generateDomainProjectId(date = new Date()) {
  const yy = String(date.getFullYear()).slice(-2);
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const randomNum = Math.floor(Math.random() * 9000) + 1000; // 4-digit random number (1000-9999)
  return `PLDMN${yy}${dd}${mm}${randomNum}`;
}

export default function AddDomainForm() {
  const { t } = useTranslation("common");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [modal, setModal] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
  });

  const [formData, setFormData] = useState({
    clientEmail: "",
    projectId: generateDomainProjectId(),
    product: "",
    renewalDate: "",
    pricePerYearEURO: "30.99",
    pricePerYearRON: "150.00",
    extendTime: "1 year",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      clientEmail: "",
      projectId: generateDomainProjectId(),
      product: "",
      renewalDate: "",
      pricePerYearEURO: "30.99",
      pricePerYearRON: "150.00",
      extendTime: "1 year",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        clientEmail: formData.clientEmail.trim().toLowerCase(),
        projectId: formData.projectId.trim(),
        product: formData.product.trim().toLowerCase(),
        renewalDate: formData.renewalDate,
        pricePerYearEURO: Number(formData.pricePerYearEURO),
        pricePerYearRON: Number(formData.pricePerYearRON),
        extendTime: formData.extendTime,
      };

      const res = await createDomainPlan(payload);
      if (!res?.success) {
        throw new Error(res?.message || "Request failed");
      }

      setModal({
        open: true,
        type: "success",
        title: t("forms.addDomain.modals.domainPlanCreated"),
        message: t("forms.addDomain.modals.successMessage"),
      });

      resetForm();
    } catch (err) {
      setModal({
        open: true,
        type: "error",
        title: t("forms.addDomain.modals.creationFailed"),
        message: err?.message || t("forms.addDomain.modals.errorMessage"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className={styles.projectForm}>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="clientEmail" className={styles.label}>
              {t("forms.addDomain.labels.clientEmail")} *
            </label>
            <input
              type="email"
              id="clientEmail"
              name="clientEmail"
              value={formData.clientEmail}
              onChange={handleInputChange}
              className={styles.input}
              placeholder={t("forms.addDomain.placeholders.clientEmailExample")}
              required
              disabled={isSubmitting}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="projectId" className={styles.label}>
              {t("forms.addDomain.labels.projectId")} *
            </label>
            <input
              type="text"
              id="projectId"
              name="projectId"
              value={formData.projectId}
              onChange={handleInputChange}
              className={styles.input}
              placeholder={t("forms.addDomain.placeholders.projectIdExample")}
              required
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="product" className={styles.label}>
              {t("forms.addDomain.labels.product")} *
            </label>
            <input
              type="text"
              id="product"
              name="product"
              value={formData.product}
              onChange={handleInputChange}
              className={styles.input}
              placeholder={t("forms.addDomain.placeholders.domainExample")}
              required
              disabled={isSubmitting}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="renewalDate" className={styles.label}>
              {t("forms.addDomain.labels.renewalDate")} *
            </label>
            <input
              type="date"
              id="renewalDate"
              name="renewalDate"
              value={formData.renewalDate}
              onChange={handleInputChange}
              className={styles.input}
              required
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="pricePerYearEURO" className={styles.label}>
              Preț/An (EUR) *
            </label>
            <input
              type="number"
              id="pricePerYearEURO"
              name="pricePerYearEURO"
              value={formData.pricePerYearEURO}
              onChange={handleInputChange}
              className={styles.input}
              placeholder="30.99"
              min="0"
              step="0.01"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="pricePerYearRON" className={styles.label}>
              Preț/An (RON) *
            </label>
            <input
              type="number"
              id="pricePerYearRON"
              name="pricePerYearRON"
              value={formData.pricePerYearRON}
              onChange={handleInputChange}
              className={styles.input}
              placeholder="150.00"
              min="0"
              step="0.01"
              required
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="extendTime" className={styles.label}>
              {t("forms.addDomain.labels.extendTime")}
            </label>
            <select
              id="extendTime"
              name="extendTime"
              value={formData.extendTime}
              onChange={handleInputChange}
              className={styles.select}
              disabled={isSubmitting}
            >
              <option value="1 year">
                {t("forms.addDomain.options.oneYear")}
              </option>
              <option value="2 years">
                {t("forms.addDomain.options.twoYears")}
              </option>
              <option value="3 years">
                {t("forms.addDomain.options.threeYears")}
              </option>
              <option value="5 years">
                {t("forms.addDomain.options.fiveYears")}
              </option>
            </select>
          </div>
        </div>

        <div className={styles.formActions}>
          <Button
            type="button"
            variant="light"
            onClick={resetForm}
            disabled={isSubmitting}
          >
            {t("forms.addDomain.buttons.resetForm")}
          </Button>
          <Button type="submit" variant="default" disabled={isSubmitting}>
            {isSubmitting
              ? t("forms.addDomain.buttons.adding")
              : t("forms.addDomain.buttons.addDomain")}
          </Button>
        </div>
      </form>

      <FeedbackModal
        open={modal.open}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText={t("forms.addDomain.modals.close")}
      />
    </>
  );
}
